/*
 * An item's rarity look (RarityEffects): a halftone behind the picture and a band at the card's foot,
 * holographic from Rare. The figures and the drawing math, pure, so a test can read them; the
 * canvases that draw them are RarityEffects'. Colors come from each canvas holder's text color (the
 * tier's `text-rarity-*`), so a tenant's rarity colors and a theme switch reach them.
 */
import { RARITY_TIERS, type RarityTier } from "./rarity.js";

/** An sRGB color as 0-255 channels. */
export type Rgb = readonly [number, number, number];

/** A point in a canvas, in CSS px. */
export interface Point {
  x: number;
  y: number;
}

/** A halftone's figures: its grid, its biggest dot, how far it reaches, how it swells and how fast. */
export interface HalftoneLook {
  /** The space between dots, px. */
  readonly cell: number;
  /** The biggest dot's radius, as a share of `cell`. */
  readonly biggest: number;
  /** How far from the center the dots reach before they vanish, as a share of the layer's width. */
  readonly reach: number;
  /** How much a dot swells and shrinks with the wave, as a share of its size. */
  readonly swell: number;
  /** How fast the wave moves. */
  readonly speed: number;
}

/**
 * The halftone behind a pull's picture: dots every 5px on a staggered grid, the biggest a radius
 * 0.384 of that spacing, shrinking to nothing half the card's width from the picture's center.
 * Each dot swells and shrinks up to 80% with a slow wave field that ripples out from the item. Its
 * strength (23%, 1.6 times hovered) is the canvas's opacity, in RarityEffects.
 */
export const HALFTONE: HalftoneLook = {
  cell: 5,
  biggest: 0.384,
  reach: 0.5,
  swell: 0.8,
  speed: 1.05,
};

/**
 * Where a halftone's dots spread from when no element is named: `square`, the middle of the square
 * at its layer's top (a pull's picture, the card's width from its top); `middle`, the layer's middle.
 */
export type HalftoneAnchor = "square" | "middle";

/**
 * Where a card's moments in the wave come from: its seed (its place in its row) times this, so
 * neighboring cards never swell in step and a reload looks the same.
 */
const PHASE_STEP = 7.3;

/**
 * Each halftone layer's moment in the wave, in seconds, for a card's `seed`: the first at the seed's
 * own step, a second (a live pull draws two, on one grid) at a repeatable random moment of its own.
 */
export function halftonePhases(seed: number, layers: number): number[] {
  const random = seededRandom(seed + 0.25);
  return Array.from({ length: layers }, (_, layer) =>
    layer === 0 ? seed * PHASE_STEP : random() * 1000,
  );
}

/** A repeatable number from 0 to 1000 for an id (`useId()`'s), so a card with no seed keeps one. */
export function seedFromId(id: string): number {
  let hashed = 0;
  for (const character of id) hashed = (hashed * 31 + (character.codePointAt(0) ?? 0)) >>> 0;
  return (hashed % 100_000) / 100;
}

/**
 * The foot band from Rare up (`wearsHolo`): drifting light patches from smooth noise over a fine
 * foil grain, at full strength and a slow pace, in the tier's own color.
 */
export const HOLO = {
  /** How far the light patches move the band's color, 0-1. */
  shine: 1,
  /** How strong the foil grain is, 0-1. */
  grain: 1,
  /** How fast the light drifts. */
  pace: 0.25,
} as const;

/** Whether a tier is at least `floor`, in the tiers' order. */
export function tierAtLeast(tier: RarityTier, floor: RarityTier): boolean {
  return RARITY_TIERS.indexOf(tier) >= RARITY_TIERS.indexOf(floor);
}

/** Whether a tier's foot band is holographic: Rare and up. */
export function wearsHolo(tier: RarityTier): boolean {
  return tierAtLeast(tier, "rare");
}

/** The halftone's rows are this much of a cell apart: a staggered grid's equal-sided triangles. */
const ROW_STEP = Math.sqrt(3) / 2;

/**
 * The dots' places in a `width` × `height` canvas: a staggered grid of `cell` with a dot on
 * `center`, every other row half a cell over, reaching a cell past each edge so a swelling dot is
 * never cut short.
 */
export function halftoneDots(
  width: number,
  height: number,
  center: Point,
  cell: number = HALFTONE.cell,
): Point[] {
  const rowStep = cell * ROW_STEP;
  const cols = Math.ceil((Math.max(center.x, width - center.x) + cell) / cell);
  const rows = Math.ceil((Math.max(center.y, height - center.y) + cell) / rowStep);
  const dots: Point[] = [];
  for (let row = -rows; row <= rows; row++) {
    const stagger = Math.abs(row) % 2 === 1 ? cell / 2 : 0;
    for (let col = -cols; col <= cols; col++) {
      dots.push({ x: center.x + col * cell + stagger, y: center.y + row * rowStep });
    }
  }
  return dots;
}

/**
 * The wave at a point and moment, about -1 to 1: two slow crossing waves and a ripple out from the
 * item (`distance` from it), so the swelling wanders rather than pulsing in step.
 */
export function halftoneWave(point: Point, distance: number, time: number): number {
  const across = Math.sin(point.x * 0.07 + time * 0.55) * Math.cos(point.y * 0.06 - time * 0.43);
  const slant = Math.sin((point.x * 0.6 - point.y * 0.8) * 0.085 + time * 0.71);
  const ripple = Math.sin(distance * 0.12 - time * 1.15);
  return (across + 0.6 * slant + 0.55 * ripple) / 2.15;
}

/**
 * A dot's radius at `distance` from the center of a layer `width` wide, at `time` (seconds, the
 * layer's own phase added; `still` leaves the wave out): the biggest at the center, nothing at the
 * reach, the reach itself breathing a little with the wave. `look` is the pulls' by default.
 */
export function halftoneRadius({
  point,
  distance,
  width,
  time,
  still,
  look = HALFTONE,
}: {
  point: Point;
  distance: number;
  width: number;
  time: number;
  still: boolean;
  look?: HalftoneLook | undefined;
}): number {
  const { cell } = look;
  const swell = still ? 0 : look.swell;
  const t = time * look.speed;
  const reach = Math.max(cell, look.reach * width * (1 + 0.1 * swell * Math.sin(t * 0.6)));
  const size = cell * look.biggest * Math.max(0, 1 - distance / reach) ** 1.2;
  return swell === 0 ? size : size * (1 + swell * halftoneWave(point, distance, t));
}

/**
 * The furthest the dots ever reach from the center of a layer `width` wide (the reach at its widest
 * breath): a dot past it never shows, so a layer leaves it out once rather than every frame.
 */
export function halftoneFurthest(
  width: number,
  still: boolean,
  look: HalftoneLook = HALFTONE,
): number {
  const swell = still ? 0 : look.swell;
  return Math.max(look.cell, look.reach * width * (1 + 0.1 * swell));
}

/*
 * The same radius, split for a canvas's frames: every term of `halftoneWave` is the sine or cosine
 * of a place part plus a time part, so by the angle sums each dot keeps its place parts' sines and
 * cosines (worked out once per size, `placeHalftoneDot`) and each frame works out the time parts
 * once (`halftoneMoment`). No sine or cosine per dot per frame; the same numbers as
 * `halftoneRadius`, which a test holds it to.
 */

/** A dot with its distance from the center and the sines and cosines of its place in the wave. */
export interface PlacedHalftoneDot extends Point {
  distance: number;
  /** The across wave's x part (`x × 0.07`): its sine, then its cosine. */
  acrossX: readonly [number, number];
  /** The across wave's y part (`y × 0.06`). */
  acrossY: readonly [number, number];
  /** The slanting wave's place part. */
  slant: readonly [number, number];
  /** The ripple's place part (`distance × 0.12`). */
  ripple: readonly [number, number];
}

/** The sine and cosine of `angle`. */
function sinCos(angle: number): readonly [number, number] {
  return [Math.sin(angle), Math.cos(angle)];
}

/** `point`, `distance` from the center, with its place in the wave worked out. */
export function placeHalftoneDot(point: Point, distance: number): PlacedHalftoneDot {
  return {
    x: point.x,
    y: point.y,
    distance,
    acrossX: sinCos(point.x * 0.07),
    acrossY: sinCos(point.y * 0.06),
    slant: sinCos((point.x * 0.6 - point.y * 0.8) * 0.085),
    ripple: sinCos(distance * 0.12),
  };
}

/** One frame's share of every dot's radius: the reach, the swell and the waves' time parts. */
export interface HalftoneMoment {
  /** The biggest dot's radius, px. */
  biggest: number;
  /** How far the dots reach this frame, px. */
  reach: number;
  /** The swell (0 when still). */
  swell: number;
  across: readonly [number, number];
  down: readonly [number, number];
  slant: readonly [number, number];
  ripple: readonly [number, number];
}

/** The frame's share at `time` (the layer's phase added) for a layer `width` wide. */
export function halftoneMoment(
  width: number,
  time: number,
  still: boolean,
  look: HalftoneLook = HALFTONE,
): HalftoneMoment {
  const swell = still ? 0 : look.swell;
  const t = time * look.speed;
  return {
    biggest: look.cell * look.biggest,
    reach: Math.max(look.cell, look.reach * width * (1 + 0.1 * swell * Math.sin(t * 0.6))),
    swell,
    across: sinCos(t * 0.55),
    down: sinCos(t * 0.43),
    slant: sinCos(t * 0.71),
    ripple: sinCos(t * 1.15),
  };
}

/** A placed dot's radius at a moment: `halftoneRadius`'s, from the parts worked out ahead. */
export function placedHalftoneRadius(dot: PlacedHalftoneDot, moment: HalftoneMoment): number {
  const fall = 1 - dot.distance / moment.reach;
  if (fall <= 0) return 0;
  const size = moment.biggest * fall ** 1.2;
  if (moment.swell === 0) return size;
  // sin(a + b), cos(c - d), sin(e + f) and sin(g - h) by the angle sums.
  const across =
    (dot.acrossX[0] * moment.across[1] + dot.acrossX[1] * moment.across[0]) *
    (dot.acrossY[1] * moment.down[1] + dot.acrossY[0] * moment.down[0]);
  const slant = dot.slant[0] * moment.slant[1] + dot.slant[1] * moment.slant[0];
  const ripple = dot.ripple[0] * moment.ripple[1] - dot.ripple[1] * moment.ripple[0];
  return size * (1 + (moment.swell * (across + 0.6 * slant + 0.55 * ripple)) / 2.15);
}

/** A repeatable random number in 0-1 for any input. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Smooth random noise in 2D, 0-1: random values on a grid, eased between. */
function noise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const at = (i: number, j: number) => hash(i * 157.31 + j * 311.71 + seed * 17.13);
  const top = at(xi, yi) + (at(xi + 1, yi) - at(xi, yi)) * u;
  const bottom = at(xi, yi + 1) + (at(xi + 1, yi + 1) - at(xi, yi + 1)) * u;
  return top + (bottom - top) * v;
}

/**
 * The light along the band at `along` (0-1) and `time`, -1 to 1: a broad slow layer of noise and
 * a finer, quicker one, so patches wander and change shape rather than slide. `seed` is the card's.
 */
export function holoLight(along: number, time: number, seed: number): number {
  const broad = noise(along * 4.5, time * 0.16, seed);
  const fine = noise(along * 10 + 31, time * 0.23 + 57, seed + 1);
  return (broad * 0.65 + fine * 0.35) * 2 - 1;
}

/**
 * A pixel's brightness on the band, 0-1 (0.5 is the tier's own color): the light, a faint bevel
 * along the top edge (`fromTop` 0 at the top, 1 at the foot), and the foil grain (`grain`, -1 to
 * 1), which glints where the light is bright.
 */
export function holoBrightness(light: number, fromTop: number, grain: number): number {
  const bevel = 0.07 * (1 - fromTop);
  const value = 0.5 + 0.36 * HOLO.shine * light + bevel;
  return Math.min(
    1,
    Math.max(0, value + grain * HOLO.grain * 0.16 * (0.35 + 0.65 * Math.max(0, light))),
  );
}

/** A small seeded random sequence (mulberry32), 0-1, for the foil grain. */
export function seededRandom(seed: number): () => number {
  let state = Math.floor(seed * 2654435761) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const toLinear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

const fromLinear = (v: number) => {
  const c = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
};

function toOklab([r8, g8, b8]: Rgb): Rgb {
  const r = toLinear(r8);
  const g = toLinear(g8);
  const b = toLinear(b8);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function fromOklab([lightness, a, b]: Rgb): Rgb {
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

/** `from` moved toward `to` by `amount` (0-1) in Oklab, as CSS's `color-mix(in oklab, …)` mixes. */
export function mixOklab(from: Rgb, to: Rgb, amount: number): Rgb {
  const x = toOklab(from);
  const y = toOklab(to);
  return fromOklab([
    x[0] + (y[0] - x[0]) * amount,
    x[1] + (y[1] - x[1]) * amount,
    x[2] + (y[2] - x[2]) * amount,
  ]);
}

const BLACK: Rgb = [0, 0, 0];
const WHITE: Rgb = [255, 255, 255];

/**
 * The band's 256 colors from dark to bright, three channels each: the tier's color at the middle
 * (128), shaded toward black below it and toward white above, never past 80% either way.
 */
export function holoPalette(tier: Rgb): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    const v = i / 255;
    const c =
      v < 0.5
        ? mixOklab(tier, BLACK, (0.5 - v) * 0.8)
        : mixOklab(tier, WHITE, Math.min(0.8, (v - 0.5) * 1.6));
    out.set(c, i * 3);
  }
  return out;
}
